import { DestroyRef, Injectable, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Firestore
} from '@angular/fire/firestore';
import { Subscription, firstValueFrom } from 'rxjs';
import { AuthStore } from '@core/services/auth/auth.store';
import { FirestoreApi } from '@core/services/data/firestore-api.service';
import { FirestorePathService } from '@core/services/data/firestore-path.service';
import { FlashbagService } from '../../../../shared/services/flashbag.service';
import { FlashbagType } from '../../../../shared/definitions/flashbag';
import { PropertyStore } from '../../../../shared/services/property.store';
import { WorkspaceStore } from '../../../../shared/services/workspace.store';
import { Repair, RepairLog, RepairStatus } from '../models/repair.model';
import type { IRepairsStore } from './repairs-store.interface';

/**
 * Firestore implementation of the workspace-scoped repairs store.
 *
 * Owns the real-time `collectionData` subscription on the workspace-scoped
 * repairs collection plus all CRUD + log subcollection I/O. Replaces the
 * legacy `RepairsService` (extended `BaseCrudService`) retired as part of
 * Phase C.
 *
 * Gating: re-subscribes when the authenticated user or `claimsReady` signal
 * change — prevents workspace-scoped reads firing before the ID token
 * carries the workspace claim.
 *
 * Logs live under `workspaces/{wid}/repairs/{repairUID}/logs`, co-located
 * with their parent repair doc so workspace-member security rules apply.
 */
@Injectable({ providedIn: 'root' })
export class RepairsFirestoreStore implements IRepairsStore, OnDestroy {
  private readonly collectionName = 'repairs';

  private readonly firestore = inject(Firestore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fs = inject(FirestoreApi);
  private readonly pathService = inject(FirestorePathService);
  private readonly authStore = inject(AuthStore);
  private readonly flashbagService = inject(FlashbagService);
  private readonly propertyStore = inject(PropertyStore);
  private readonly workspaceStore = inject(WorkspaceStore);

  private readonly _repairs = signal<readonly Repair[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly repairs = this._repairs.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._repairs().length);
  readonly openCount = computed(() =>
    this._repairs().filter(r => r.status !== RepairStatus.Completed && r.status !== RepairStatus.Canceled).length
  );
  readonly completedCount = computed(() =>
    this._repairs().filter(r => r.status === RepairStatus.Completed).length
  );

  readonly counts = computed(() => {
    const out = {
      pending: 0,
      inProgress: 0,
      completed: 0,
      canceled: 0,
      waitingForParts: 0,
      assigned: 0,
      resolved: 0,
      total: 0
    };
    for (const r of this._repairs()) {
      out.total++;
      switch (r.status) {
        case RepairStatus.Pending: out.pending++; break;
        case RepairStatus.InProgress: out.inProgress++; break;
        case RepairStatus.Completed: out.completed++; break;
        case RepairStatus.Canceled: out.canceled++; break;
        case RepairStatus.WaitingForParts: out.waitingForParts++; break;
        case RepairStatus.Assigned: out.assigned++; break;
        case RepairStatus.Resolved: out.resolved++; break;
      }
    }
    return out;
  });

  private sub?: Subscription;
  /** (user, workspace) scope the live listener is currently open on — guards
   *  `fetchAll` against re-subscribing (and re-paying the whole collection)
   *  when the scope is unchanged. Mirrors base-crud.service.ts:499. */
  private lastFetchedScopeKey: string | null = null;

  constructor() {
    effect(() => {
      const user = this.authStore.user();
      const claimsReady = this.authStore.claimsReady();
      if (!user?.userUID) {
        this.clearRepairs();
        return;
      }
      if (!claimsReady) return;
      this.fetchAll();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  async refresh(force = false): Promise<void> {
    this.fetchAll(force);
  }

  getRepairById(repairUID: string): Repair | null {
    return this._repairs().find(r => r.repairUID === repairUID) ?? null;
  }

  getRepairsByProperty(propertyUID: string): readonly Repair[] {
    return this._repairs().filter(r => r.propertyUID === propertyUID);
  }

  getRepairsByStatus(status: RepairStatus): readonly Repair[] {
    return this._repairs().filter(r => r.status === status);
  }

  async addRepair(repair: Repair): Promise<string> {
    if (!this.isAuthenticated()) throw new Error('User not authenticated');

    try {
      if (repair.propertyUID && (!repair.propertyName || repair.propertyName.trim() === '')) {
        const name = this.propertyStore.getPropertyNameByUID(repair.propertyUID);
        if (name !== 'Unknown Property') repair.propertyName = name;
      }

      const repairData = this.addTimestamps({ ...repair }, false);
      // Same `undefined` defence as `updateRepair` — addDoc rejects
      // undefined field values too.
      const sanitized = stripUndefined(repairData as Record<string, unknown>);
      const docRef = await this.fs.addDoc(this.getCollection(), sanitized);
      // Optimistic update — prepend to the signal so the UI reacts without
      // waiting for the live listener to emit. Dedup guard: addDoc writes to
      // the local Firestore cache synchronously, so the `collectionData`
      // listener (idField 'repairUID') can already have set the list with this
      // doc by the time we get here. Without the guard we'd prepend a second
      // copy that sticks until a refresh (the server snapshot matches the cache
      // and never re-emits). Mirrors the loans.service.ts addLoan guard.
      const newRepair = { ...repair, repairUID: docRef.id } as Repair;
      if (!this._repairs().some(r => r.repairUID === newRepair.repairUID)) {
        this._repairs.set([newRepair, ...this._repairs()]);
      }
      this.flashbagService.messageToDisplay('SAVED', 'Repair has been successfully saved', FlashbagType.Success, 4000);
      return docRef.id;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this._lastError.set(error);
      this.flashbagService.messageToDisplay('ERROR', 'Failed to save repair', FlashbagType.Danger, 4000);
      throw error;
    }
  }

  async updateRepair(repair: Repair): Promise<void> {
    if (!repair.repairUID) throw new Error('Repair UID is required for updates');
    if (!this.isAuthenticated()) throw new Error('User not authenticated');

    try {
      if (repair.status === RepairStatus.Completed && !repair.completedDate) {
        repair.completedDate = new Date();
      }
      if (repair.propertyUID && (!repair.propertyName || repair.propertyName.trim() === '')) {
        const name = this.propertyStore.getPropertyNameByUID(repair.propertyUID);
        if (name !== 'Unknown Property') repair.propertyName = name;
      }

      const updateData = this.addTimestamps({ ...repair }, true);
      // Firestore rejects literal `undefined` values. Strip them — fields
      // the caller wants to clear must be passed as `deleteField()`
      // sentinels, not undefined.
      const sanitized = stripUndefined(updateData as Record<string, unknown>);
      await this.fs.updateDoc(this.getDocument(repair.repairUID), sanitized);
      // Optimistic update — mirror the same semantics for the local
      // signal: undefined means "leave the existing value alone", and
      // any FieldValue sentinel (deleteField, etc.) means "drop the
      // field on the local copy too" so the UI doesn't briefly render
      // the sentinel object.
      this._repairs.set(
        this._repairs().map(r =>
          r.repairUID === repair.repairUID ? mergeForOptimistic(r, repair) : r
        )
      );
      this.flashbagService.messageToDisplay('UPDATED', 'Repair has been successfully updated', FlashbagType.Success, 4000);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this._lastError.set(error);
      this.flashbagService.messageToDisplay('ERROR', 'Failed to update repair', FlashbagType.Danger, 4000);
      throw error;
    }
  }

  async deleteRepair(repairUID: string): Promise<void> {
    if (!this.isAuthenticated()) throw new Error('User not authenticated');

    try {
      await this.fs.deleteDoc(this.getDocument(repairUID));
      // Optimistic update.
      this._repairs.set(this._repairs().filter(r => r.repairUID !== repairUID));
      this.flashbagService.messageToDisplay('DELETED', 'Repair has been successfully deleted', FlashbagType.Success, 4000);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this._lastError.set(error);
      this.flashbagService.messageToDisplay('ERROR', 'Failed to delete repair', FlashbagType.Danger, 4000);
      throw error;
    }
  }

  async getRepairLogs(repairUID: string): Promise<readonly RepairLog[]> {
    const user = this.authStore.user();
    if (!user?.userUID) return [];

    const logCollection = this.getLogCollection(repairUID);
    const logQuery = this.fs.query(logCollection, this.fs.orderBy('date', 'desc'));

    const rows = await firstValueFrom(this.fs.collectionData(logQuery, { idField: 'logUID' }));
    return rows.map(raw => {
      const r = raw as Record<string, unknown>;
      const log = new RepairLog(repairUID, r['action'] as string, r['notes'] as string | undefined);
      log.logUID = r['logUID'] as string;
      log.userUID = r['userUID'] as string;
      log.userName = r['userName'] as string;
      log.documentUIDs = (r['documentUIDs'] as string[]) || [];
      log.date = this.parseDate(r['date']) || new Date();
      return log;
    });
  }

  async addGeneralLog(repairUID: string, action: string, notes?: string, documentUIDs?: string[]): Promise<string> {
    const user = this.authStore.user();
    if (!user?.userUID) throw new Error('User must be authenticated to add repair logs');

    try {
      const log = new RepairLog(repairUID, action, notes);
      if (documentUIDs && documentUIDs.length > 0) log.documentUIDs = documentUIDs;

      const logData = {
        date: new Date(),
        action: log.action,
        notes: log.notes,
        userUID: user.userUID,
        userName: user.email || 'User',
        documentUIDs: log.documentUIDs || []
      };

      const logCollection = this.getLogCollection(repairUID);
      const ref = await this.fs.addDoc(logCollection, logData);
      return ref.id;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this._lastError.set(error);
      throw error;
    }
  }

  // --- internals ---

  private fetchAll(force = false): void {
    if (!this.isAuthenticated()) return;
    const scopeKey = `${this.authStore.user()?.userUID}:${this.workspaceStore.currentWorkspaceId() ?? ''}`;
    // Already streaming this exact (user, workspace) scope — the live listener
    // already reflects every change, so re-subscribing only re-pays the whole
    // collection. Skip unless a manual refresh forces it.
    if (!force && this.sub && !this.sub.closed && this.lastFetchedScopeKey === scopeKey) {
      return;
    }
    this.sub?.unsubscribe();
    this.lastFetchedScopeKey = scopeKey;
    this._loading.set(true);
    try {
      const ref = this.getCollection();
      this.sub = this.fs.collectionData(ref, { idField: 'repairUID' })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
        next: rows => {
          const repairs = rows.map(data => this.buildRepair(data));
          this._repairs.set(repairs);
          this._loading.set(false);
        },
        error: err => {
          this._lastError.set(err instanceof Error ? err : new Error(String(err)));
          this._loading.set(false);
        }
      });
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      this._loading.set(false);
    }
  }

  private buildRepair(data: unknown): Repair {
    const d = data as Record<string, unknown>;
    const repair = new Repair(
      d['propertyUID'] as string,
      d['title'] as string,
      d['description'] as string,
      this.parseDate(d['reportedDate']) || new Date()
    );
    Object.assign(repair, d);
    // Parse common date fields. createdAt/updatedAt must be coerced to Date —
    // downstream views (e.g. the interventions hub sort) call `.getTime()` on
    // them, which throws on a raw Firestore Timestamp and renders an empty list.
    repair.reportedDate = this.parseDate(d['reportedDate']) || repair.reportedDate;
    repair.createdAt = this.parseDate(d['createdAt']) || repair.createdAt;
    repair.updatedAt = this.parseDate(d['updatedAt']) || repair.updatedAt;
    if (d['completedDate']) repair.completedDate = this.parseDate(d['completedDate']) || undefined;
    if (d['scheduledDate']) {
      const indexable = repair as unknown as Record<string, unknown>;
      indexable['scheduledDate'] = this.parseDate(d['scheduledDate']) || indexable['scheduledDate'];
    }
    // Backfill property name when missing
    if (repair.propertyUID && (!repair.propertyName || repair.propertyName.trim() === '')) {
      const name = this.propertyStore.getPropertyNameByUID(repair.propertyUID);
      if (name !== 'Unknown Property') repair.propertyName = name;
    }
    return repair;
  }

  private clearRepairs(): void {
    this.sub?.unsubscribe();
    this.lastFetchedScopeKey = null;
    this._repairs.set([]);
  }

  private getCollection() {
    return this.fs.collection(this.firestore, this.pathService.getWorkspaceCollection(this.collectionName));
  }

  private getDocument(repairUID: string) {
    return this.fs.doc(this.firestore, this.pathService.getWorkspaceDocument(this.collectionName, repairUID));
  }

  /**
   * Log subcollection path: workspaces/{wid}/repairs/{repairUID}/logs —
   * co-located with the parent repair doc so the workspace-member Firestore
   * rules apply (the catch-all `/{collection}/{docId}/{subcollection}/{subdocId}`
   * fallback grants read to members / write to workspace editors).
   *
   * The previous user-scoped path `users/{uid}/repairs/{repairUID}/logs` had
   * NO matching security rule after the workspace migration, so every read and
   * write was denied with "Missing or insufficient permissions".
   */
  private getLogCollection(repairUID: string) {
    return this.fs.collection(this.getDocument(repairUID), 'logs');
  }

  private isAuthenticated(): boolean {
    return !!this.authStore.user()?.userUID;
  }

  private parseDate(dateValue: unknown): Date | null {
    if (!dateValue) return null;
    if (typeof dateValue === 'object' && dateValue !== null && 'toDate' in dateValue && typeof (dateValue as { toDate?: unknown }).toDate === 'function') {
      return (dateValue as { toDate: () => Date }).toDate();
    }
    if (dateValue instanceof Date) return dateValue;
    if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      const d = new Date(dateValue);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  private addTimestamps<T extends Record<string, unknown>>(data: T, isUpdate = false): T & {
    createdAt?: ReturnType<FirestoreApi['serverTimestamp']>;
    updatedAt: ReturnType<FirestoreApi['serverTimestamp']>;
  } {
    const result = { ...data, updatedAt: this.fs.serverTimestamp() };
    if (!isUpdate) {
      (result as Record<string, unknown>)['createdAt'] = this.fs.serverTimestamp();
    }
    return result;
  }
}

/**
 * Drop keys whose value is `undefined` — Firestore's updateDoc rejects
 * those with "Unsupported field value: undefined". To explicitly clear
 * a field, callers should pass `deleteField()` instead of `undefined`.
 */
export function stripUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * Heuristic — non-null objects that aren't Date / Array / plain POJOs are
 * treated as Firestore sentinels (`deleteField()`, `serverTimestamp()`,
 * `arrayUnion()`, etc.). Sentinels make sense to send to Firestore but
 * should never leak into the local signal cache.
 */
export function isFieldValueSentinel(value: unknown): boolean {
  if (value === null || typeof value !== 'object') return false;
  if (value instanceof Date) return false;
  if (Array.isArray(value)) return false;
  if (Object.getPrototypeOf(value) === Object.prototype) return false;
  return true;
}

/**
 * Optimistic merge — mirrors `updateDoc` semantics on the local copy:
 *   - `undefined` ⇒ leave the existing value alone
 *   - `deleteField()` (and other sentinels) ⇒ drop the field
 *   - everything else ⇒ overwrite
 */
export function mergeForOptimistic(existing: Repair, patch: Partial<Repair>): Repair {
  const merged = { ...existing } as Record<string, unknown>;
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if (isFieldValueSentinel(value)) {
      delete merged[key];
      continue;
    }
    merged[key] = value;
  }
  return merged as unknown as Repair;
}
